
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { PlayerHelper } from '../Player/player-helper';
import { ErrorCatalog } from '../../util/error-catalog';
import RestrictionContainer from './RestrictionContainer';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';

interface ErrorContainerProps {
	isMobile?: boolean;
	playerConfigs?: any
}

const ErrorMessage = styled.div`
    color: white;
    font-size: 20px;
    line-height: 20px;
    font-weight: 600;
    text-align: center;
    padding: 5px;
    color: #fff;
`
const ErrorCode = styled.div`
    color: white;
    font-size: 20px;
    line-height: 20px;
    font-weight: normal;
    text-align: center;
    padding: 5px;
`

const { ERROR_TYPES, ERROR_LABELS } = PlayerHelper;

// Restrictions that carry no more information than the HTTP status they were derived from.
const GENERIC_RESTRICTION_TYPES: string[] = [ERROR_TYPES.AccessForbidden, ERROR_TYPES.AuthorizationRequired];

const ErrorContainer: React.FC<ErrorContainerProps> = ({ isMobile, playerConfigs }) => {
    const { t } = useTranslation();
    const { errorDetails, currentMediaId, isMediaLive, showRegistrationForm, setTransitionLayer, setShowCountdownModule, setCountdownStartTime } = useConfiguration();
    const { setRemovePoster } = usePlayer();

    const errorMessage = useMemo(() => {
        const currentMediaError = errorDetails.find((error: any) => error.mediaId === currentMediaId);
        if (!currentMediaError) {
            return null;
        }

        const { details } = currentMediaError;
        const { type, data, exception } = details;
        const loadedUrl = data?.url || exception?.data?.url || "";
        let code = currentMediaError?.code || exception?.code || "";

        let message = "";
        let errorType = type;
        let countdownValue: string | null = null;
        // Set when the failure could be pinned to a known resource + HTTP status, in which
        // case the viewer sees "Error code: <source> <status>" instead of the bare "(code)".
        let displayCode = "";

        // GoBrain always appends a generic restriction to a 401/403 (AuthorizationRequired /
        // AccessForbidden) on top of any specific one it could determine. Only a specific
        // restriction is allowed to take over the message, because the generic ones say no more
        // than the HTTP status already does and would mask the status-specific wording below.
        const specificRestriction = (data?.restrictions ?? []).find(
            (restriction: any) => restriction?.type && !GENERIC_RESTRICTION_TYPES.includes(restriction.type)
        );
        if (specificRestriction) {
            errorType = specificRestriction.type;
        }

        const responseBody = data?.response ?? exception?.data?.response;

        // Geo and domain blocks have no restriction of their own, so they have to be read off the
        // 403 body. A real encoding 403 is `{"publish": "<message>"}`, which GoBrain already maps
        // to MediaEncoding, so this only has to cover the keys the mapper does not know about.
        const forbiddenReason = Number(code) === 403
            ? ErrorCatalog.classifyForbidden(responseBody)
            : null;
        if (forbiddenReason) {
            return {
                message: forbiddenReason === ErrorCatalog.FORBIDDEN_REASONS.geo
                    ? ERROR_LABELS.geoRestricted
                    : ERROR_LABELS.domainRestricted,
                code,
                countdownValue: null,
                displayCode: "",
            };
        }

        switch (errorType) {
            case ERROR_TYPES.MediaEncoding:
                message = ERROR_LABELS.encoding;
                break;
            case ERROR_TYPES.AccessForbidden:
                message = ERROR_LABELS.forbidden;
                break;
            case ERROR_TYPES.DataLoad:
            case ERROR_TYPES.MediaPlayerLoad: {
                if (!loadedUrl && exception?.type === ERROR_TYPES.VideoJsLoad) {
                    message = isMediaLive ? ERROR_LABELS.liveCannotPlay : ERROR_LABELS.videoCannotPlay;
                    code = code || "404";
                    break;
                }
                // A 416 on a segment means a corrupt CDN cache rather than a plain range
                // error, so it keeps its own dedicated message.
                if (Number(code) === 416) {
                    message = ERROR_LABELS.cndCache;
                    break;
                }

                const classified = ErrorCatalog.classify({
                    url: loadedUrl,
                    status: code,
                    requestType: data?.requestType ?? exception?.data?.requestType,
                });
                if (classified) {
                    message = classified.messageKey;
                    displayCode = classified.displayCode;
                    break;
                }

                // Fall back to the older, less specific wording when the URL or the status
                // could not be classified (e.g. a network-level failure reporting code 0).
                if (loadedUrl.includes("/medias/")) {
                    message = ERROR_LABELS.mediaNotFound;
                    break;
                }
                if (Number(code) === 524) {
                    message = isMediaLive ? ERROR_LABELS.liveTimeout : ERROR_LABELS.videoTimeout;
                    break;
                }

                message = isMediaLive ? ERROR_LABELS.liveCannotPlay : ERROR_LABELS.videoCannotPlay;
                code = code || "404";
                break;
            }
            case ERROR_TYPES.MediaUnavailableBefore:
                message = ERROR_LABELS.cannotBeViewedYet;
                if (specificRestriction?.value) {
                    countdownValue = specificRestriction.value;
                }
                break;
            case ERROR_TYPES.AuthorizationRequired:
                message = ERROR_LABELS.notRecognised;
                break;
            case ERROR_TYPES.IPRangeUnsatisfied:
                message = ERROR_LABELS.ipRestriction;
                break;
            case ERROR_TYPES.MediaUnavailableAfter:
                message = ERROR_LABELS.notAnymore;
                break;
            case ERROR_TYPES.VideoCorrupted:
                message = ERROR_LABELS.videoCorrupted;
                break;
            default:
                message = ERROR_LABELS.mediaNotFound;
                code = code || "404";
                break;
        }

        return { message, code, countdownValue, displayCode };
    }, [currentMediaId, errorDetails, isMediaLive]);

    useEffect(() => {
        if (!errorMessage) {
            return;
         }
        setTransitionLayer(false);
        setRemovePoster(true);
        if (errorMessage.countdownValue && !showRegistrationForm) {
            setShowCountdownModule(true);
            setCountdownStartTime(errorMessage.countdownValue);
        }
    }, [errorMessage, showRegistrationForm]); // eslint-disable-line react-hooks/exhaustive-deps

    if (errorMessage?.countdownValue) {
        return null;
    }

    return errorMessage?.message && errorMessage.message !== ERROR_LABELS.cannotBeViewedYet ? (
        <RestrictionContainer isMobile={isMobile} playerConfigs={playerConfigs}>
            <ErrorMessage>{t(errorMessage.message)} </ErrorMessage>
            {errorMessage.displayCode
                ? <ErrorCode>{t(ErrorCatalog.ERROR_CODE_LABEL, { code: errorMessage.displayCode })}</ErrorCode>
                : errorMessage.code ? <ErrorCode>{`(${errorMessage.code})`}</ErrorCode> : null}
        </RestrictionContainer>
    ) : null;
}

export default ErrorContainer;
